/**
 * Pantalla 7 (docs/FASE-1.md §6): `/health`, `/ready` y estado del repartidor de webhooks.
 *
 * Los conteos de entregas por estado vienen de `GET /api/v1/webhooks/health`
 * (`apps/idp/src/platform/audit-log.ts`), que los agrega por módulo en una sola consulta. La
 * señal que importa vigilar es `oldestPendingAt`: si envejece, el repartidor está atascado.
 */
import type { WebhookModuleHealth } from "@continnua/contracts";
import { useEffect, useState, type ReactElement } from "react";

import { healthApi, webhookHealthApi, type HealthStatus, type ReadyStatus } from "../api/client";
import { Badge } from "../components/Badge";
import { toDisplayMessage } from "../lib/errors";
import { formatDateTime } from "../lib/format";

export function Health(): ReactElement {
  const [health, setHealth] = useState<HealthStatus | null>(null);
  const [ready, setReady] = useState<ReadyStatus | null>(null);
  const [summaries, setSummaries] = useState<WebhookModuleHealth[]>([]);
  const [checkedAt, setCheckedAt] = useState<string | null>(null);
  const [loadingWebhooks, setLoadingWebhooks] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [healthResult, readyResult] = await Promise.allSettled([healthApi.health(), healthApi.ready()]);
      if (cancelled) return;
      setHealth(healthResult.status === "fulfilled" ? healthResult.value : null);
      setReady(readyResult.status === "fulfilled" ? readyResult.value : { status: "unavailable" });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadWebhookHealth(): Promise<void> {
      setLoadingWebhooks(true);
      try {
        const result = await webhookHealthApi.get();
        if (!cancelled) {
          setSummaries(result.modules);
          setCheckedAt(result.checkedAt);
        }
      } catch (err) {
        if (!cancelled) setError(toDisplayMessage(err));
      } finally {
        if (!cancelled) setLoadingWebhooks(false);
      }
    }
    void loadWebhookHealth();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section>
      <div className="page-header">
        <h1>Salud</h1>
      </div>

      <div className="health-cards">
        <div className="health-card">
          <p className="subtle">/health</p>
          {health ? <Badge tone={health.status === "ok" ? "success" : "danger"}>{health.status}</Badge> : <Badge tone="danger">sin respuesta</Badge>}
        </div>
        <div className="health-card">
          <p className="subtle">/ready (base de datos)</p>
          {ready ? <Badge tone={ready.status === "ok" ? "success" : "danger"}>{ready.status}</Badge> : <Badge tone="danger">sin respuesta</Badge>}
        </div>
      </div>

      <h2>Repartidor de webhooks</h2>
      {checkedAt && <p className="subtle">Datos calculados el {formatDateTime(checkedAt)}.</p>}
      {error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}
      {loadingWebhooks ? (
        <p>Cargando…</p>
      ) : summaries.length === 0 ? (
        <p className="notice" role="status">
          Ningún módulo tiene endpoints de webhook registrados todavía.
        </p>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th scope="col">Módulo</th>
              <th scope="col">Endpoints activos</th>
              <th scope="col">Deshabilitados</th>
              <th scope="col">Pendientes</th>
              <th scope="col">Fallidas</th>
              <th scope="col">Entregadas</th>
              <th scope="col">Pendiente más antigua</th>
            </tr>
          </thead>
          <tbody>
            {summaries.map((summary) => (
              <tr key={summary.moduleCode}>
                <td>{summary.moduleCode}</td>
                <td>{summary.endpointsActive}</td>
                <td>{summary.endpointsDisabled}</td>
                <td>
                  {summary.pending > 0 ? <Badge tone="warning">{summary.pending}</Badge> : summary.pending}
                </td>
                <td>{summary.failed > 0 ? <Badge tone="danger">{summary.failed}</Badge> : summary.failed}</td>
                <td>{summary.delivered}</td>
                <td>{summary.oldestPendingAt ? formatDateTime(summary.oldestPendingAt) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
